import {
  EasyTramPlan,
  EasyTramSegmentType,
  EASYTRAMPLANSTATE,
  Field,
  ScenarioBorder,
  ScenarioBorderRing,
  ScenarioBorderSegment,
  ScenarioLine,
} from '../../fields/field.model';
import {
  DefaultSpreadingType,
  SelectedSpreadingType,
  SpreadingType,
} from '../../spreading-type-selector/spreading-type';
import {
  drawLine,
  drawMarker,
} from '../map-drawing-logic';
import { DOTTYPE } from '../map-state-control';
import { MapComponent } from '../map.component';
import { createInsideControlLine } from '../scenario/map-drawing-logic-insideControl';
import {
  easyTramHeadlandTramlineColor,
  easyTramHeadlandGuidanceTrackColor,
  easyTramMainlandGuidanceTrackColor,
  easyTramMainlandTramlineColor,
  easyTramMainlandBoundaryColor,
  easyTramMainlandBaseSegmentColor,
  easyTramMainlandCoverageColor,
  drawLineDefaultColor,
} from '../map-drawing-colors';

function colorForSegmentType(segmentType: EasyTramSegmentType): string {
  switch (segmentType) {
    case 'headlandGuidanceTrack':
      return easyTramHeadlandGuidanceTrackColor;
    case 'headlandTramline':
      return easyTramHeadlandTramlineColor;
    case 'mainlandGuidanceTrack':
      return easyTramMainlandGuidanceTrackColor;
    case 'mainlandTramline':
      return easyTramMainlandTramlineColor;
    case 'mainlandBoundary':
      return easyTramMainlandBoundaryColor;
    case 'mainlandBaseSegment':
      return easyTramMainlandBaseSegmentColor;
    case 'mainlandCoverage':
      return easyTramMainlandCoverageColor;
    case 'default':
      return drawLineDefaultColor;
  }
}

function drawSegmentLine(
  mapInstance: MapComponent,
  segment: ScenarioBorderSegment,
  borderRing: ScenarioBorderRing,
  cornerPoints: google.maps.LatLng[]
): ScenarioLine {
  const startOfLine = new google.maps.LatLng(
    segment.coordinates[0][1]!,
    segment.coordinates[0][0]!
  );
  const endOfLine = new google.maps.LatLng(
    segment.coordinates[1][1]!,
    segment.coordinates[1][0]!
  );
  const newestLineObj = createFieldLineFromScenarioElement(
    mapInstance,
    startOfLine,
    endOfLine,
    segment.getSpreadingType(),
    segment.easyTramSegmentType,
    borderRing,
    cornerPoints
  );
  return newestLineObj;
}

function checkIsDotFieldCorner(
  dotPos: google.maps.LatLng,
  borderPath: google.maps.LatLng[]
): boolean {
  for (const latLng of borderPath || []) {
    if (latLng.lat() == dotPos!.lat() && latLng.lng() == dotPos!.lng()) {
      return true;
    }
  }
  return false;
}

function isCorelandBaseSegmentChanging(
  borderRing: ScenarioBorderRing,
  dotPos: google.maps.LatLng
): boolean {
  for (let idx = 0; idx < borderRing.segments.length; idx++) {
    const line = borderRing.segments[idx];
    const lineEnd = line.coordinates[1];

    if (dotPos.lng() == lineEnd[0] && dotPos.lat() == lineEnd[1]) {
      const nextIdx = idx == borderRing.segments.length - 1 ? 0 : idx + 1;
      return line.easyTramSegmentType != borderRing.segments[nextIdx].easyTramSegmentType;
    }
  }
  return false;
}

export function createFieldLineFromScenarioElement(
  mapInstance: MapComponent,
  startOfLine: google.maps.LatLng,
  endOfLine: google.maps.LatLng,
  spreadingType: SelectedSpreadingType,
  easyTramSegmentType: EasyTramSegmentType,
  borderRing: ScenarioBorderRing,
  cornerPoints: google.maps.LatLng[]
): ScenarioLine {
  const isModeChanging = isCorelandBaseSegmentChanging(borderRing, endOfLine);
  const scenarioLine: ScenarioLine = {
    id: crypto.randomUUID(),
    dotEnd: drawMarker(
      mapInstance.map,
      checkIsDotFieldCorner(endOfLine, cornerPoints)
        ? DOTTYPE.End
        : DOTTYPE.ScenarioSplit,
      endOfLine,
      undefined, //no event Handler
      isModeChanging ? 'dotScenarioSplit.svg' : null
    ),
    line: drawLine(
      mapInstance.map,
      [startOfLine!, endOfLine],
      colorForSegmentType(easyTramSegmentType),
      {
        clickable: false,
        strokeWeight: (easyTramSegmentType !== 'default') ? 2 : 6,
        icons: [{
          icon: { path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 4, strokeColor: colorForSegmentType(easyTramSegmentType) },
          offset: '50%',
        }],
      }
    ),
    lineData: {
      spreadingType: spreadingType,
      easyTramSegmentType: easyTramSegmentType,
    },
  };

  //TODO: check infacing handling
  if (spreadingType.infacing == SpreadingType.InsideControl) {
    scenarioLine.line!.insideControlLine = createInsideControlLine(
      mapInstance,
      scenarioLine,
      borderRing
    ); //new line is getting returned
  }

  return scenarioLine;
}

export function drawEasyTramPolygon(
  mapInstance: MapComponent,
  easyTramPlan: EasyTramPlan
) {
  const easyTramObj = easyTramPlan.clone();
  console.log('Drawing EasyTramPlan object: ', easyTramObj);

  for (const polygon of mapInstance.scenarioData.polygons) {
    polygon.setMap(null);
  }

  mapInstance.scenarioData.lines = []; //reset scenarioData Lines!
  mapInstance.scenarioData.polygons = []; //reset scenarioData Polygons!

  const paths = mapInstance.selectedField?.getPaths4Polygon()!;
  const cornerPoints = paths[0]!;
  // get all segments from the structure and draw them
  let lineStore: ScenarioLine[] = [];
  for (const element of easyTramObj!.border.outer.segments) {
    lineStore.push(
      drawSegmentLine(
        mapInstance,
        element,
        easyTramObj!.border.outer,
        cornerPoints
      )
    );
  }
  mapInstance.scenarioData.lines.push(lineStore);

  if (easyTramObj!.border.inners !== undefined) {
    //we might have exclusions
    for (
      let exclusionIndex = 0;
      exclusionIndex < easyTramObj!.border.inners.length;
      exclusionIndex++
    ) {
      const exclusion = easyTramObj!.border.inners[exclusionIndex];
      const exclusionPoints = paths[exclusionIndex + 1]!; //paths contains outer border as well!
      lineStore = [];
      for (const element of exclusion.segments) {
        lineStore.push(
          drawSegmentLine(mapInstance, element, exclusion, exclusionPoints)
        );
      }
      mapInstance.scenarioData.lines.push(lineStore);
    }
  }

  // Workaround because without the clone there is an error
  const easyTramClone = easyTramObj.clone();

  if (
    easyTramClone.validateMachineConfiguration()
    && easyTramClone.validateHeadlandConfiguration()
    && easyTramClone.state >= EASYTRAMPLANSTATE.HeadlandCreation
  ) {

    if (easyTramClone.headlandType !== 'off' &&  easyTramClone.headlandWidth > 0) {
      // Draw mainland boundary if different from field boundary
      const mainlandBoundary = easyTramClone.mainlandBoundary();
      if (mainlandBoundary !== undefined) {
        lineStore = [];
        for (const segment of mainlandBoundary.segments) {
          // TODO Do not clone the segments?
          const segClone = segment.clone();
          lineStore.push(
            drawSegmentLine(
              mapInstance,
              segClone,
              mainlandBoundary,
              cornerPoints
            )
          );
        }
        mapInstance.scenarioData.lines.push(lineStore);
      }
    }

    // Draw headland guidance track
    const headlandGuidanceTracks = easyTramClone.headlandGuidanceTracks();
    if (headlandGuidanceTracks !== undefined) {
      for (const guidanceTrack of headlandGuidanceTracks) {
        lineStore = [];
        for (const segment of guidanceTrack.segments) {
          // TODO Do not clone the segments?
          const segClone = segment.clone();
          lineStore.push(
            drawSegmentLine(
              mapInstance,
              segClone,
              guidanceTrack,
              cornerPoints
            )
          );
        }
        mapInstance.scenarioData.lines.push(lineStore);
      }
    }

    // Draw headland tramlines
    const headlandTramlineTracks = easyTramClone.headlandTramlinePolygons();
    if (headlandTramlineTracks !== undefined) {
      for (const headlandPolygons of headlandTramlineTracks) {
        for (const headlandPolygonSide of headlandPolygons) {
          lineStore = [];
          for (const segment of headlandPolygonSide.segments) {
            const segClone = segment.clone();
            lineStore.push(
              drawSegmentLine(
                mapInstance,
                segClone,
                headlandPolygonSide,
                cornerPoints
              )
            );
          }
          mapInstance.scenarioData.lines.push(lineStore);
        }
      }
    }
  }

  if (
    easyTramClone.validateMachineConfiguration() 
    && easyTramClone.validateHeadlandConfiguration()
    && easyTramClone.validateCorelandConfiguration()
    && easyTramClone.state >= EASYTRAMPLANSTATE.CorelandCreation
  ) {
    // Draw mainland guidance tracks
    const mainlandGuidanceTracks = easyTramClone.mainlandGuidanceTracks();
    if (mainlandGuidanceTracks !== undefined) {
      for (const guidanceTrack of mainlandGuidanceTracks) {
        lineStore = [];
        for (const segment of guidanceTrack.segments) {
          // TODO Do not clone the segments?
          const segClone = segment.clone();
          lineStore.push(
            drawSegmentLine(
              mapInstance,
              segClone,
              guidanceTrack,
              cornerPoints
            )
          );
        }
        mapInstance.scenarioData.lines.push(lineStore);
      }
    }

    // Draw mainland tramlines
    const mainlandTramlineTracks = easyTramClone.mainlandTramlinePolygons();
    if (mainlandTramlineTracks !== undefined) {
      for (const mainlandPolygon of mainlandTramlineTracks) {
        for (const mainlandPolygonSide of mainlandPolygon) {
          lineStore = [];
          for (const segment of mainlandPolygonSide.segments) {
            const segClone = segment.clone();
            lineStore.push(
              drawSegmentLine(
                mapInstance,
                segClone,
                mainlandPolygonSide,
                cornerPoints
              )
            );
          }
          mapInstance.scenarioData.lines.push(lineStore);
        }
      }
    }

    // Draw mainland coverage if headland is not off and coverage is shown
    const mainlandCoveragePolygons = easyTramClone.mainlandCoveragePolygons();
    if (mainlandCoveragePolygons !== undefined && easyTramClone.showFieldCoverage === true) {
      for (const coveragePolygon of mainlandCoveragePolygons) {
        const paths = coveragePolygon
          .segments.flatMap(points =>  {
            return points.coordinates.map(point => ({ lat: point[1], lng: point[0] }))
        });

        const poly = new google.maps.Polygon({
          paths,
          clickable: false,
          draggable: false,
          geodesic: true,
          strokeWeight: 0,
          strokeColor: easyTramMainlandCoverageColor,
          strokeOpacity: 0.5,
          fillColor: easyTramMainlandCoverageColor,
          fillOpacity: 0.5,
          map: mapInstance.map,
        });
        mapInstance.scenarioData.polygons.push(poly);
      }
    }

    // Draw headland coverage if headland is not off and coverage is shown
    const headlandCoveragePolygons = easyTramClone.headlandCoveragePolygons();
    if (headlandCoveragePolygons !== undefined && easyTramClone.showFieldCoverage === true) {
      for (const coveragePolygon of headlandCoveragePolygons) {
        const paths = coveragePolygon
          .segments.flatMap(points =>  {
            return points.coordinates.map(point => ({ lat: point[1], lng: point[0] }))
        });

        const poly = new google.maps.Polygon({
          paths,
          clickable: false,
          draggable: false,
          geodesic: true,
          strokeWeight: 0,
          strokeColor: easyTramMainlandCoverageColor,
          strokeOpacity: 0.5,
          fillColor: easyTramMainlandCoverageColor,
          fillOpacity: 0.5,
          map: mapInstance.map,
        });
        mapInstance.scenarioData.polygons.push(poly);
      }
    }

  }

}

export function createNewEasyTramPlanFromField(field: Field): EasyTramPlan {
  const border = new ScenarioBorder();
  border.outer = new ScenarioBorderRing();
  border.outer.segments = [];
  border.inners = [];
  let segments = border.outer.segments;

  // use outer boundary
  let isExclusion = false;
  for (const coordinates of field.polygon.coordinates) {
    if (isExclusion) {
      segments = [];
      const ring = new ScenarioBorderRing();
      ring.segments = segments;
      border.inners.push(ring);
    }

    // create segments for all fieldLines in the scenarioData.lines
    for (let i = 0; i < coordinates.length - 1; i++) {
      const sbs = new ScenarioBorderSegment();
      sbs.coordinates = [coordinates[i], coordinates[i + 1]];
      // get the outfacing spreading type (name)
      sbs.setSpreadingType(DefaultSpreadingType);
      segments.push(sbs);
    }
    isExclusion = true;
  }

  const easyTram = new EasyTramPlan(border);
  easyTram.state = EASYTRAMPLANSTATE.MachineSelection;
  return easyTram;
}
